<template>
  <BaseModule
    v-bind="$props"
    class="imagens-carrossel"
    data-module="destinations"
  >
    <header
      v-if="title || subtitle || text"
      class="header content base-title-fontsize"
    >
      <component
        :is="titleTag"
        v-if="title"
        class="tit tit-g"
      >
        {{ title }}
      </component>
      <h3
        v-if="subtitle"
        class="sub"
      >
        {{ subtitle }}
      </h3>
      <div
        v-if="text"
        class="txt"
      >
        {{ text }}
      </div>
    </header>

    <Warning
      v-if="items.length === 0 && editing"
      text="O módulo não possui nenhum item adicionado"
    >
    </Warning>

    <!-- Slider main container -->
    <div
      ref="swiper"
      class="swiper"
    >
      <!-- Additional required wrapper -->
      <div class="swiper-wrapper carrossel-list">
        <!-- Slides -->
        <div
          v-for="(item, idx) in items"
          :key="item.id || idx"
          class="swiper-slide"
        >
          <div class="grid-block">
            <div
              class="block"
              :class="{
                'block--hover':
                  showButton(item.buttonText, item.linkUrl) ||
                  hasItemDescription(item.text),
              }"
            >
              <figure
                class="bg"
                :style="{ backgroundImage: `url('${item.image}')` }"
              ></figure>

              <div
                v-if="
                  hasTitle(item.title) ||
                  showButton(item.buttonText, item.linkUrl)
                "
                class="grid-overlay"
                :class="{ 'grid-overlay--title': hasTitle(item.title) }"
              >
                <h3
                  v-if="hasTitle(item.title)"
                  class="name base-content-fontsize"
                >
                  {{ item.title }}
                </h3>

                <div class="wrapper">
                  <p
                    v-if="hasItemDescription(item.text)"
                    class="text"
                  >
                    {{ item.text }}
                  </p>

                  <EditorLink
                    v-if="showButton(item.buttonText, item.linkUrl)"
                    :model="item.linkUrl"
                    class="cta"
                  >
                    {{ item.buttonText }}
                  </EditorLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- If we need navigation buttons -->
      <div class="content buttons">
        <div :class="`custom-button-prev btn-prev-${instanceId}`">
          &#60; {{ $t('prev') }}
        </div>
        <div :class="`custom-button-next btn-next-${instanceId}`">
          {{ $t('next') }} &#62;
        </div>
      </div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';

import showButton from '@/mixins/showButton';
import base from '@/mixins/base';
import Warning from '@/components/Warning.vue';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

export default {
  name: 'Destinations',
  components: {
    BaseModule,
    EditorLink,
    Warning,
  },
  mixins: [showButton, base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    backgroundColor: {
      type: String,
      default: '#ffffff',
    },
    text: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      swiper: null,
    };
  },
  computed: {
    editing() {
      return typeof this.$parent === 'undefined';
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    currentEditableItem() {
      this.slideToActivatedItem();
    },
    items() {
      this.initSwiper();
    },
  },
  mounted() {
    this.initSwiper();
  },
  methods: {
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('items')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );
          if (this.swiper) {
            this.swiper.slideTo(itemPathIndex);
          }
        }
      }
    },
    hasTitle(title) {
      return typeof title === 'string' && /\S/.test(title);
    },
    hasItemDescription(description) {
      return typeof description === 'string' && /\S/.test(description);
    },
    initSwiper() {
      if (this.swiper) {
        this.swiper.destroy();
      }
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs.swiper, {
          modules: [Navigation],
          centerInsufficientSlides: true,
          breakpoints: {
            320: {
              slidesPerView: 1,
              slidesPerGroup: 1,
            },
            640: {
              slidesPerView: 2,
              slidesPerGroup: 2,
            },
            1024: {
              slidesPerView: 'auto',
              slidesPerGroup: 3,
            },
          },
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });
        this.slideToActivatedItem();
      });
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'text',
            fields: [
              {
                name: 'subtitle',
                maxlength: 115,
              },
              {
                name: 'text',
                type: 'LongText',
                label: 'Texto de apoio',
              },
            ],
          },
          {
            name: 'items',
            label: 'Itens',
            fields: [
              {
                name: 'items',
                type: 'List',
                minItems: 1,
                itemLabel({ title }) {
                  return title || 'Item {index}'; // any {index} will be replaced by item's index (1-based)
                },
                addItemLabel: 'Adicionar item',
                fields: [
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Título',
                    maxlength: 30,
                  },
                  {
                    name: 'text',
                    type: 'LongText',
                    label: 'Texto de apoio',
                    maxlength: 200,
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem de fundo',
                    helpText: 'Dimensões recomendadas: 500x500',
                  },
                  {
                    name: 'buttonText',
                    type: 'ShortText',
                    label: 'Texto do botão',
                    maxlength: 20,
                  },
                  {
                    name: 'linkUrl',
                    type: 'Url',
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            toRemove: ['buttonColor', 'buttonTextColor'],
          },
        ],
      },
    ],
  },
  i18n: {
    messages: {
      pt: {
        next: 'Avançar',
        prev: 'Voltar',
      },
      en: {
        next: 'Next',
        prev: 'Prev',
      },
      es: {
        next: 'Avance',
        prev: 'Regresar',
      },
    },
  },
};
</script>

<style scoped lang="scss">
.swiper-slide {
  height: 300px;
}

@include media-breakpoint-down(sm) {
  .swiper-slide {
    width: 100% !important;
  }
}

@include media-breakpoint-between(sm, lg) {
  .swiper-slide {
    width: 50% !important;
  }
}

@include media-breakpoint-up(lg) {
  .swiper-slide {
    width: 33.333% !important;
  }
}

.custom-button-prev,
.custom-button-next {
  color: var(--text-color);
  font-size: $size-small;
  cursor: pointer;

  &.swiper-button-disabled {
    display: none;
  }
}

.custom-button-next {
  margin-left: auto;
}
</style>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.imagens-carrossel {
  overflow: hidden;
  padding-left: 0;
  padding-right: 0;
  padding: 0 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  position: relative;
  width: 100%;

  &::before,
  &::after {
    background-color: var(--bg-color);
    opacity: 0.85;
    content: '';
    display: block;
    position: absolute;
    top: 0;
    width: 50%;
    height: 100%;
    z-index: 2;
  }

  &::before {
    right: calc(50% + 489px);
  }

  &::after {
    left: calc(50% + 489px);
  }

  .header {
    margin-bottom: 32px;

    .txt {
      margin: 0 auto;
      max-width: 700px;
      text-align: center;
      white-space: pre-line;
      font-size: $size-small;
      line-height: 1.5em;
    }
  }

  .swiper {
    max-width: 980px;
    overflow: visible;
  }

  .carrossel-list {
    height: 300px;
    margin: 0;
    position: relative;

    .grid-block {
      float: left;
      height: 100%;
      width: 100%;
      position: relative;

      .block {
        overflow: hidden;
        position: relative;
        width: 100%;
        height: inherit;

        &--hover:hover {
          .bg {
            transform: scale(1.3) rotate(2deg);
            transition: transform 15s cubic-bezier(0.13, 0.82, 0.43, 0.88);
          }

          .grid-overlay {
            height: 100%;

            .wrapper {
              max-height: 300px;

              > * :nth-child(2) {
                transition-delay: 300ms;
              }

              > * {
                opacity: 1;
                filter: none;
                transform: translateY(0);
                transition: all 400ms;
                color: $lb-white;
              }
            }
          }
        }

        .bg {
          position: absolute;
          top: 0;
          left: 0;
          transform: scale(1);
          transition: transform 800ms linear;
          z-index: 0;
          background-position: center;
          background-size: cover;
          background-repeat: no-repeat;
          font-size: 0;
          width: 100%;
          height: 100%;
        }

        .grid-overlay {
          background: rgba(0, 0, 0, 0.7);
          color: $lb-white;
          position: absolute;
          left: 0;
          bottom: 0;
          text-align: center;
          transition: height 300ms ease-out;
          height: 0px;
          width: 100%;
          display: flex;
          flex-direction: column;
          justify-content: center;

          &--title {
            height: 70px;
          }

          .wrapper {
            margin: 0 auto;
            max-height: 0;
            max-width: 80%;
            overflow: hidden;
            transition: max-height 300ms ease-out;
            width: 380px;

            > * {
              margin-top: 15px;
              opacity: 0;
              filter: alpha(opacity=0);
              transform: translateY(50px);
              transition: all 200ms;
            }
          }

          .cta {
            padding: 0;
            text-decoration: none;
            background-color: var(--primary-color);
          }

          .name {
            color: $lb-white;
            font-weight: $lb-bold;
          }
        }
      }
    }
  }

  .buttons {
    display: flex;
    margin-top: 20px;
  }
}

@include media-breakpoint-down(sm) {
  .imagens-carrossel {
    padding: 0 20px;
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}

@include media-breakpoint-down(md) {
  .carrossel-list {
    .grid-block {
      width: 100%;
      max-width: none;

      .block {
        .grid-overlay {
          justify-content: center;
          align-items: center;
          text-align: center;

          .wrapper {
            > * {
              margin: 15px auto 0;
              opacity: 1;
              position: static;
              transform: none;
            }
          }
        }
      }

      .cta {
        width: auto !important;
      }
    }
  }
}
</style>
